import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const GALLERY_CATEGORIES = ["Exteriors", "Kitchens", "Interiors", "Gardens", "Projects"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function GalleryManager() {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("Exteriors");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [baItems, setBaItems] = useState([]);
  const [baLabel, setBaLabel] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [baUploading, setBaUploading] = useState(false);

  const load = () => api.get("/admin/gallery").then(r => setItems(r.data.items || [])).catch(() => {});
  const loadBa = () => api.get("/admin/gallery/before-after").then(r => setBaItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); loadBa(); }, []);

  const pickImg = (f, set) => {
    if (!f) return;
    if (!/\.(jpe?g|png|webp)$/i.test(f.name)) { toast.error("Use a JPG, PNG or WebP image."); return; }
    set(f);
  };

  const uploadBa = async () => {
    if (!beforeFile || !afterFile) { toast.error("Choose both a before and an after photo."); return; }
    setBaUploading(true);
    try {
      const fd = new FormData();
      fd.append("before", beforeFile);
      fd.append("after", afterFile);
      fd.append("label", baLabel);
      await api.post("/admin/gallery/before-after", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Before & after pair added");
      setBeforeFile(null); setAfterFile(null); setBaLabel("");
      loadBa();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBaUploading(false);
    }
  };

  const removeBa = async (id) => {
    try { await api.delete(`/admin/gallery/before-after/${id}`); setBaItems(baItems.filter(i => i.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  const pick = (f) => {
    if (!f) return;
    if (!/\.(jpe?g|png|webp)$/i.test(f.name)) { toast.error("Use a JPG, PNG or WebP image."); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!file) { toast.error("Choose an image first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("category", category);
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to gallery");
      setFile(null); setCaption("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/gallery/${id}`); setItems(items.filter(i => i.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-4">
        <div
          data-testid="gallery-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
          className={`border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors ${dragging ? "border-[#C79A3B] bg-[#F7ECE3]" : "border-[#E5E7EB] bg-white"}`}
        >
          <p className="text-sm font-bold">{file ? file.name : "Drag & drop a project photo here"}</p>
          <p className="text-xs text-[#6B7280] mt-1">JPG, PNG or WebP</p>
          <label className="de-btn-secondary mt-4 cursor-pointer inline-flex">
            Browse files
            <input data-testid="gallery-file-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          </label>
        </div>

        <div className="border border-[#E5E7EB] bg-white p-4 space-y-3">
          <div>
            <div className="label-eyebrow mb-1">Caption</div>
            <input data-testid="gallery-caption" value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Rear kitchen extension, Bracknell" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Category</div>
            <select data-testid="gallery-category" value={category} onChange={e => setCategory(e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm bg-white">
              {GALLERY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button data-testid="gallery-upload-btn" disabled={uploading} onClick={upload} className="de-btn-primary w-full justify-center">{uploading ? "Uploading…" : "Add to gallery"}</button>
          <p className="text-xs text-[#6B7280]">Uploads appear first on the public <b>/gallery</b> page. The built-in starter photos always remain.</p>
        </div>
      </div>

      <div className="label-eyebrow mt-8 mb-3">Uploaded photos ({items.length})</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {items.map(it => (
          <div key={it.id} data-testid={`gallery-admin-item-${it.id}`} className="border border-[#E5E7EB] bg-white overflow-hidden group relative">
            <img src={`${BACKEND}/api/public/gallery/file/${it.id}`} alt={it.caption} className="w-full h-32 object-cover" />
            <div className="p-2">
              <div className="text-xs font-bold truncate">{it.caption || "—"}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">{it.category}</div>
            </div>
            <button data-testid={`gallery-delete-${it.id}`} onClick={() => remove(it.id)} className="absolute top-1 right-1 bg-white/90 border border-[#E5E7EB] text-[10px] font-bold uppercase px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#C79A3B] hover:text-white">Delete</button>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full p-6 text-center text-[#6B7280] text-sm">No uploaded photos yet — the public gallery shows the built-in starter set.</div>}
      </div>

      <div className="border-t border-[#E5E7EB] mt-10 pt-8" data-testid="admin-before-after">
        <div className="label-eyebrow mb-1">Before &amp; after pairs</div>
        <p className="text-xs text-[#6B7280] mb-4">Upload a matched <b>before</b> and <b>after</b> photo of the same property. These appear in the “Before &amp; After” block at the top of the public <b>/gallery</b> page. The built-in example pairs always remain.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <BaPick label="Before photo" file={beforeFile} onPick={(f) => pickImg(f, setBeforeFile)} testid="ba-before" />
          <BaPick label="After photo" file={afterFile} onPick={(f) => pickImg(f, setAfterFile)} testid="ba-after" />
          <div className="space-y-3">
            <div>
              <div className="label-eyebrow mb-1">Caption</div>
              <input data-testid="ba-label" value={baLabel} onChange={e => setBaLabel(e.target.value)} placeholder="e.g. Semi-detached, Wokingham" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
            </div>
            <button data-testid="ba-upload-btn" disabled={baUploading} onClick={uploadBa} className="de-btn-primary w-full justify-center">{baUploading ? "Uploading…" : "Add pair"}</button>
          </div>
        </div>

        <div className="label-eyebrow mt-8 mb-3">Your before &amp; after pairs ({baItems.length})</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {baItems.map(it => (
            <div key={it.id} data-testid={`ba-admin-item-${it.id}`} className="border border-[#E5E7EB] bg-white overflow-hidden group relative">
              <div className="grid grid-cols-2">
                <img src={`${BACKEND}/api/public/gallery/ba-file/${it.id}/before`} alt="before" className="w-full h-28 object-cover" />
                <img src={`${BACKEND}/api/public/gallery/ba-file/${it.id}/after`} alt="after" className="w-full h-28 object-cover" />
              </div>
              <div className="p-2 text-xs font-bold truncate">{it.label || "—"}</div>
              <button data-testid={`ba-delete-${it.id}`} onClick={() => removeBa(it.id)} className="absolute top-1 right-1 bg-white/90 border border-[#E5E7EB] text-[10px] font-bold uppercase px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#C79A3B] hover:text-white">Delete</button>
            </div>
          ))}
          {baItems.length === 0 && <div className="col-span-full p-6 text-center text-[#6B7280] text-sm">No custom pairs yet — the gallery shows the built-in example transformations.</div>}
        </div>
      </div>
    </div>
  );
}

function BaPick({ label, file, onPick, testid }) {
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <label className={`border-2 border-dashed flex flex-col items-center justify-center text-center p-5 cursor-pointer transition-colors ${file ? "border-[#C79A3B] bg-[#F7ECE3]" : "border-[#E5E7EB] bg-white hover:border-[#C79A3B]"}`}>
        <span className="text-xs font-bold truncate max-w-full">{file ? file.name : "Choose image"}</span>
        <span className="text-[10px] text-[#6B7280] mt-1">JPG, PNG or WebP</span>
        <input data-testid={`${testid}-input`} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
      </label>
    </div>
  );
}
